/**
 * @fileoverview One packet or plant row of a "Check and save" card or the seed-packet review: tick · cut-out · name
 * · › (D78 rev 2 §3, owner 2026-10-22: "clicking the seed packet (or plant) should bring up a dialog where they can
 * alter it").
 *
 * The tick at the left is its own target (include it or not). The rest of the row is one button that opens the row's
 * change dialog (RowChangeDialog), named for screen readers "Change Tomato · Cherokee Purple". Under the name, the
 * lines there are to say: a kind's plant and variety, "Which plant is this?" for a packet Grow could not place (which
 * cannot be ticked until it has a plant), and the row's own bed ("Herbs (its own bed)"), only when it has one. The
 * picture is the row's own part of the photo, a picture only (B59), passed in by the card.
 *
 * Where: PacketPile and KindsPile (photo-tray), PacketReview.
 * API: none.
 * Words (en/es): packets.changeRow, tray.kinds.newToGrow.
 *
 * @module PacketRow
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Pill } from '@/shared/components/ui/Pill';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** What a row draws. */
interface PacketRowProps {
  /** For the tick's id. */
  id: string;
  /** Its picture: the row's own cut-out, or the whole photo's thumbnail. */
  picture: React.ReactNode;
  /** Its name in one line ("Tomato · Cherokee Purple"). */
  name: string;
  /** A line under the name: its plant and variety, or a question ("Which plant is this?"). */
  detail?: React.ReactNode;
  /** Its own bed, when it has one ("Herbs (its own bed)"): bold in the warm colour, as KindsPile's own beds always were. */
  bedLine?: string | null;
  /** The mark after the name: a plant new to Grow; none otherwise (B83: no tag for a new variety). */
  mark?: 'newToGrow' | null;
  /** Ticked: it is filed. */
  ticked: boolean;
  /** False for a packet Grow could not place yet: no tick until it has a plant. */
  tickable: boolean;
  /** Ticks or unticks it. */
  onTick: (on: boolean) => void;
  /** Opens its change dialog. */
  onOpen: () => void;
  /** True while the card is busy. */
  disabled?: boolean;
}

/**
 * One row: tick · picture · name · ›.
 *
 * @param props - PacketRowProps.
 */
export const PacketRow: React.FC<PacketRowProps> = ({
  id, picture, name, detail = null, bedLine = null, mark = null, ticked, tickable, onTick, onOpen, disabled = false,
}) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <li className="flex min-h-11 items-center gap-1 rounded-sm border">
      {/* The tick: its own target, the full height of the row. */}
      <label htmlFor={id} className={cn('flex min-h-11 shrink-0 items-center self-stretch pl-2.5 pr-1.5', tickable && !disabled && 'cursor-pointer')}>
        <input
          id={id}
          type="checkbox"
          className="h-5 w-5 accent-primary"
          checked={tickable && ticked}
          onChange={(event) => onTick(event.target.checked)}
          disabled={!tickable || disabled}
          aria-label={name}
        />
      </label>
      {/* The rest of the row opens its change dialog. */}
      <button
        type="button"
        onClick={onOpen}
        disabled={disabled}
        aria-label={t('packets.changeRow', { name })}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-3 py-1.5 pr-2 text-left disabled:opacity-60"
      >
        <span className="shrink-0" aria-hidden="true">{picture}</span>
        <span className="min-w-0 flex-1 text-sm leading-snug">
          <span className="font-medium">{name}</span>
          {mark && (
            <Pill size="sm" tone="primary" className="ml-1.5 align-middle">
              {t('tray.kinds.newToGrow')}
            </Pill>
          )}
          {detail && <span className="block text-xs text-muted-foreground">{detail}</span>}
          {bedLine && <span className="block text-xs font-bold text-verdict-warm">{bedLine}</span>}
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
    </li>
  );
};
